// CoursesAbout.js - Courses themed About section component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a courses themed About section with institute profile and core values
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the about is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The about elements
 */
export const createCoursesAboutElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const aboutSectionClass = 'courses-about-section';
  const aboutContainerClass = 'courses-about-container';
  const aboutHeaderClass = 'courses-about-header';
  const aboutTitleClass = 'courses-about-title';
  const aboutSubtitleClass = 'courses-about-subtitle';
  const aboutContentClass = 'courses-about-content';
  const aboutLeftClass = 'courses-about-left';
  const aboutRightClass = 'courses-about-right';
  const aboutImageWrapperClass = 'courses-about-image-wrapper';
  const aboutImageClass = 'courses-about-image';
  const aboutStoryClass = 'courses-about-story';
  const aboutStoryTitleClass = 'courses-about-story-title';
  const aboutStoryTextClass = 'courses-about-story-text';
  const aboutValuesClass = 'courses-about-values';
  const aboutValuesTitleClass = 'courses-about-values-title';
  const aboutValuesListClass = 'courses-about-values-list';
  const aboutValueItemClass = 'courses-about-value-item';
  const aboutValueIconClass = 'courses-about-value-icon';
  const aboutValueNameClass = 'courses-about-value-name';
  const aboutValueDescClass = 'courses-about-value-desc';
  const aboutQuoteClass = 'courses-about-quote';
  const aboutQuoteTextClass = 'courses-about-quote-text';
  const aboutQuoteAuthorClass = 'courses-about-quote-author';

  // Desktop Styles - Courses theme with indigo/purple gradients
  const aboutSectionStyles = {
    position: 'relative',
    paddingTop: '120px',
    paddingBottom: '120px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f8fafc',
    background: `
      radial-gradient(circle at 80% 20%, rgba(79, 70, 229, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 20% 80%, rgba(99, 102, 241, 0.05) 0%, transparent 50%),
      linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #f8fafc 100%)
    `,
    color: '#1e293b',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden'
  };

  const aboutContainerStyles = {
    maxWidth: '1400px',
    width: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '80px',
    position: 'relative',
    zIndex: '1'
  };

  const aboutHeaderStyles = {
    textAlign: 'center',
    marginBottom: '60px'
  };

  const aboutTitleStyles = {
    fontSize: '56px',
    fontWeight: '700',
    color: '#1e293b',
    marginBottom: '24px',
    letterSpacing: '-0.02em',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutSubtitleStyles = {
    fontSize: '20px',
    fontWeight: '400',
    color: '#64748b',
    lineHeight: '1.6',
    maxWidth: '600px',
    margin: '0 auto',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutContentStyles = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '80px',
    alignItems: 'start'
  };

  const aboutLeftStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px',
    background: `
      linear-gradient(135deg, rgba(79, 70, 229, 0.08) 0%, rgba(99, 102, 241, 0.04) 100%),
      rgba(255, 255, 255, 0.9)
    `,
    padding: '40px',
    borderRadius: '12px',
    border: '1px solid rgba(79, 70, 229, 0.2)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
  };

  const aboutRightStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '40px'
  };

  const aboutImageWrapperStyles = {
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: `
      0 20px 40px -8px rgba(0, 0, 0, 0.15),
      0 0 0 1px rgba(79, 70, 229, 0.3),
      0 0 30px rgba(79, 70, 229, 0.1)
    `,
    border: '2px solid #4f46e5',
    alignSelf: 'center',
    maxWidth: '300px',
    width: '100%'
  };

  const aboutImageStyles = {
    width: '100%',
    height: '350px',
    objectFit: 'cover',
    transition: 'all 0.6s ease',
    filter: 'contrast(1.1) brightness(1.05)'
  };

  const aboutStoryStyles = {
    background: `
      linear-gradient(135deg, rgba(79, 70, 229, 0.08) 0%, rgba(99, 102, 241, 0.04) 100%),
      rgba(255, 255, 255, 0.9)
    `,
    padding: '40px',
    borderRadius: '12px',
    border: '1px solid rgba(79, 70, 229, 0.2)',
    backdropFilter: 'blur(10px)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
  };

  const aboutStoryTitleStyles = {
    fontSize: '32px',
    fontWeight: '700',
    color: '#4f46e5',
    marginBottom: '24px',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutStoryTextStyles = {
    fontSize: '16px',
    lineHeight: '1.8',
    color: '#475569',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutValuesStyles = {
    background: 'transparent',
    padding: '0',
    borderRadius: '0',
    border: 'none',
    backdropFilter: 'none'
  };

  const aboutValuesTitleStyles = {
    fontSize: '28px',
    fontWeight: '700',
    color: '#4f46e5',
    marginBottom: '32px',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutValuesListStyles = {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  };

  const aboutValueItemStyles = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '16px',
    padding: '20px',
    background: `
      linear-gradient(135deg, rgba(79, 70, 229, 0.05) 0%, rgba(99, 102, 241, 0.02) 100%),
      rgba(255, 255, 255, 0.8)
    `,
    borderRadius: '8px',
    border: '1px solid rgba(79, 70, 229, 0.15)',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)'
  };

  const aboutValueIconStyles = {
    width: '48px',
    height: '48px',
    backgroundColor: '#4f46e5',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    color: '#ffffff',
    flexShrink: '0'
  };

  const aboutValueNameStyles = {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1e293b',
    marginBottom: '8px',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutValueDescStyles = {
    fontSize: '14px',
    lineHeight: '1.6',
    color: '#64748b',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutQuoteStyles = {
    textAlign: 'center',
    padding: '60px 40px',
    background: `
      linear-gradient(135deg, rgba(79, 70, 229, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%),
      rgba(255, 255, 255, 0.95)
    `,
    borderRadius: '12px',
    border: '1px solid rgba(79, 70, 229, 0.25)',
    backdropFilter: 'blur(20px)',
    marginTop: '40px',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)'
  };

  const aboutQuoteTextStyles = {
    fontSize: '24px',
    fontWeight: '500',
    color: '#1e293b',
    lineHeight: '1.6',
    fontStyle: 'italic',
    marginBottom: '24px',
    fontFamily: '"Inter", sans-serif'
  };

  const aboutQuoteAuthorStyles = {
    fontSize: '16px',
    color: '#4f46e5',
    fontWeight: '600',
    fontFamily: '"Inter", sans-serif'
  };

  // Hover styles
  const aboutValueItemHoverStyles = {
    background: `
      linear-gradient(135deg, rgba(79, 70, 229, 0.1) 0%, rgba(99, 102, 241, 0.05) 100%),
      rgba(255, 255, 255, 0.95)
    `,
    border: '1px solid rgba(79, 70, 229, 0.3)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(79, 70, 229, 0.15)'
  };

  // Tablet Styles
  const tabletAboutContentStyles = {
    ...aboutContentStyles,
    gridTemplateColumns: '1fr',
    gap: '60px'
  };

  const tabletAboutTitleStyles = {
    ...aboutTitleStyles,
    fontSize: '48px'
  };

  const tabletAboutLeftStyles = {
    ...aboutLeftStyles,
    padding: '32px'
  };

  const tabletAboutStoryStyles = {
    ...aboutStoryStyles,
    padding: '32px'
  };

  // Mobile Styles
  const mobileAboutSectionStyles = {
    ...aboutSectionStyles,
    paddingTop: '80px',
    paddingBottom: '80px'
  };

  const mobileAboutContainerStyles = {
    ...aboutContainerStyles,
    gap: '60px',
    padding: '0 20px'
  };

  const mobileAboutTitleStyles = {
    ...aboutTitleStyles,
    fontSize: '36px'
  };

  const mobileAboutSubtitleStyles = {
    ...aboutSubtitleStyles,
    fontSize: '18px'
  };

  const mobileAboutLeftStyles = {
    ...aboutLeftStyles,
    padding: '24px'
  };

  const mobileAboutStoryStyles = {
    ...aboutStoryStyles,
    padding: '24px'
  };

  const mobileAboutQuoteStyles = {
    ...aboutQuoteStyles,
    padding: '40px 24px'
  };

  const mobileAboutQuoteTextStyles = {
    ...aboutQuoteTextStyles,
    fontSize: '20px'
  };

  const mobileAboutValueItemStyles = {
    ...aboutValueItemStyles,
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    padding: '20px 16px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(aboutSectionClass, aboutSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutContainerClass, aboutContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutHeaderClass, aboutHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutTitleClass, aboutTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutSubtitleClass, aboutSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutContentClass, aboutContentStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutLeftClass, aboutLeftStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutRightClass, aboutRightStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutImageWrapperClass, aboutImageWrapperStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutImageClass, aboutImageStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutStoryClass, aboutStoryStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutStoryTitleClass, aboutStoryTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutStoryTextClass, aboutStoryTextStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutValuesClass, aboutValuesStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutValuesTitleClass, aboutValuesTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutValuesListClass, aboutValuesListStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutValueItemClass, aboutValueItemStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutValueIconClass, aboutValueIconStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutValueNameClass, aboutValueNameStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutValueDescClass, aboutValueDescStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutQuoteClass, aboutQuoteStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutQuoteTextClass, aboutQuoteTextStyles);
    newRegistry[DEVICES.DESKTOP].set(aboutQuoteAuthorClass, aboutQuoteAuthorStyles);

    // Hover styles
    newRegistry[DEVICES.DESKTOP].set(`${aboutValueItemClass}--hover`, aboutValueItemHoverStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(aboutContentClass, tabletAboutContentStyles);
    newRegistry[DEVICES.TABLET].set(aboutTitleClass, tabletAboutTitleStyles);
    newRegistry[DEVICES.TABLET].set(aboutLeftClass, tabletAboutLeftStyles);
    newRegistry[DEVICES.TABLET].set(aboutStoryClass, tabletAboutStoryStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(aboutSectionClass, mobileAboutSectionStyles);
    newRegistry[DEVICES.MOBILE].set(aboutContainerClass, mobileAboutContainerStyles);
    newRegistry[DEVICES.MOBILE].set(aboutTitleClass, mobileAboutTitleStyles);
    newRegistry[DEVICES.MOBILE].set(aboutSubtitleClass, mobileAboutSubtitleStyles);
    newRegistry[DEVICES.MOBILE].set(aboutLeftClass, mobileAboutLeftStyles);
    newRegistry[DEVICES.MOBILE].set(aboutStoryClass, mobileAboutStoryStyles);
    newRegistry[DEVICES.MOBILE].set(aboutQuoteClass, mobileAboutQuoteStyles);
    newRegistry[DEVICES.MOBILE].set(aboutQuoteTextClass, mobileAboutQuoteTextStyles);
    newRegistry[DEVICES.MOBILE].set(aboutValueItemClass, mobileAboutValueItemStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    aboutSectionClass,
    aboutContainerClass,
    aboutHeaderClass,
    aboutTitleClass,
    aboutSubtitleClass,
    aboutContentClass,
    aboutLeftClass,
    aboutRightClass,
    aboutImageWrapperClass,
    aboutImageClass,
    aboutStoryClass,
    aboutStoryTitleClass,
    aboutStoryTextClass,
    aboutValuesClass,
    aboutValuesTitleClass,
    aboutValuesListClass,
    aboutValueItemClass,
    aboutValueIconClass,
    aboutValueNameClass,
    aboutValueDescClass,
    aboutQuoteClass,
    aboutQuoteTextClass,
    aboutQuoteAuthorClass
  ]));

  // Generate IDs for all elements
  const aboutSectionId = Date.now();
  const containerId = aboutSectionId + 1;
  const headerId = aboutSectionId + 2;
  const titleId = aboutSectionId + 3;
  const subtitleId = aboutSectionId + 4;
  const contentId = aboutSectionId + 5;
  const leftId = aboutSectionId + 6;
  const rightId = aboutSectionId + 7;
  const imageWrapperId = aboutSectionId + 8;
  const imageId = aboutSectionId + 9;
  const valuesId = aboutSectionId + 10;
  const valuesTitleId = aboutSectionId + 11;
  const valuesListId = aboutSectionId + 12;
  const value1Id = aboutSectionId + 13;
  const value1IconId = aboutSectionId + 14;
  const value1ContentId = aboutSectionId + 15;
  const value1NameId = aboutSectionId + 16;
  const value1DescId = aboutSectionId + 17;
  const value2Id = aboutSectionId + 18;
  const value2IconId = aboutSectionId + 19;
  const value2ContentId = aboutSectionId + 20;
  const value2NameId = aboutSectionId + 21;
  const value2DescId = aboutSectionId + 22;
  const value3Id = aboutSectionId + 23;
  const value3IconId = aboutSectionId + 24;
  const value3ContentId = aboutSectionId + 25;
  const value3NameId = aboutSectionId + 26;
  const value3DescId = aboutSectionId + 27;
  const value4Id = aboutSectionId + 28;
  const value4IconId = aboutSectionId + 29;
  const value4ContentId = aboutSectionId + 30;
  const value4NameId = aboutSectionId + 31;
  const value4DescId = aboutSectionId + 32;
  const storyId = aboutSectionId + 33;
  const storyTitleId = aboutSectionId + 34;
  const storyTextId = aboutSectionId + 35;
  const quoteId = aboutSectionId + 36;
  const quoteTextId = aboutSectionId + 37;
  const quoteAuthorId = aboutSectionId + 38;

  const elements = [
    // Main About Section
    {
      id: aboutSectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Courses About Section',
      parentId: null,
      className: aboutSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'courses-about-section',
        'data-component': 'section',
        'data-section-type': 'about',
        'data-page-id': currentPageId
      }
    },

    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Container',
      parentId: aboutSectionId,
      hierarchyName: "About Container",
      className: aboutContainerClass,
      styles: {},
      children: [headerId, contentId, quoteId],
      text: '',
      attributes: {
        'data-element': 'about-container'
      }
    },

    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Header',
      parentId: containerId,
      hierarchyName: "About Header",
      className: aboutHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'about-header'
      }
    },

    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'About Title',
      parentId: headerId,
      hierarchyName: "About Title",
      className: aboutTitleClass,
      styles: {},
      children: [],
      text: 'About Skilled Nation Institute of Technology',
      attributes: {
        'data-element': 'about-title'
      }
    },

    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'About Subtitle',
      parentId: headerId,
      hierarchyName: "About Subtitle",
      className: aboutSubtitleClass,
      styles: {},
      children: [],
      text: 'Bridging the gap between academic knowledge and practical industry demands through high-quality, career-oriented education.',
      attributes: {
        'data-element': 'about-subtitle'
      }
    },

    // Content
    {
      id: contentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Content',
      parentId: containerId,
      hierarchyName: "About Content",
      className: aboutContentClass,
      styles: {},
      children: [leftId, rightId],
      text: '',
      attributes: {
        'data-element': 'about-content'
      }
    },

    // Left Column
    {
      id: leftId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Left',
      parentId: contentId,
      hierarchyName: "About Left",
      className: aboutLeftClass,
      styles: {},
      children: [imageWrapperId, valuesId],
      text: '',
      attributes: {
        'data-element': 'about-left'
      }
    },

    // Right Column
    {
      id: rightId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Right',
      parentId: contentId,
      hierarchyName: "About Right",
      className: aboutRightClass,
      styles: {},
      children: [storyId],
      text: '',
      attributes: {
        'data-element': 'about-right'
      }
    },

    // Image Wrapper
    {
      id: imageWrapperId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Image Wrapper',
      parentId: leftId,
      hierarchyName: "About Image Wrapper",
      className: aboutImageWrapperClass,
      styles: {},
      children: [imageId],
      text: '',
      attributes: {
        'data-element': 'about-image-wrapper'
      }
    },

    // About Image
    {
      id: imageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'About Image',
      parentId: imageWrapperId,
      hierarchyName: "About Image",
      className: aboutImageClass,
      styles: {},
      children: [],
      text: '',
      src: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?q=80&w=2071&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      attributes: {
        'data-element': 'about-image',
        'alt': 'Students learning at Skilled Nation Institute of Technology'
      }
    },

    // Story Section
    {
      id: storyId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Story',
      parentId: rightId,
      hierarchyName: "About Story",
      className: aboutStoryClass,
      styles: {},
      children: [storyTitleId, storyTextId],
      text: '',
      attributes: {
        'data-element': 'about-story'
      }
    },

    // Story Title
    {
      id: storyTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Story Title',
      parentId: storyId,
      hierarchyName: "Story Title",
      className: aboutStoryTitleClass,
      styles: {},
      children: [],
      text: 'Our Mission & Vision',
      attributes: {
        'data-element': 'about-story-title'
      }
    },

    // Story Text
    {
      id: storyTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Story Text',
      parentId: storyId,
      hierarchyName: "Story Text",
      className: aboutStoryTextClass,
      styles: {},
      children: [],
      text: 'Established in 2019, Skilled Nation Institute of Technology was founded with the mission to bridge the gap between academic knowledge and practical industry demands. We are dedicated to delivering high-quality, career-oriented education that empowers students with the technical expertise, problem-solving abilities, and professional skills required in today\'s dynamic job market.\n\nAt Skilled Nation, we pride ourselves on creating a learning environment that encourages innovation, creativity, and hands-on experience. Our programs are designed in close collaboration with industry experts to ensure that our curriculum stays relevant and future-ready. Whether it\'s information technology, engineering, software development, or other emerging technologies, we strive to prepare our students not just for jobs, but for lifelong success in their chosen fields.\n\nWe believe in a "Skill First" philosophy — focusing on practical training, live projects, and real-world applications to enhance employability and entrepreneurship. Through our qualified faculty, modern infrastructure, and strong industry connections, we ensure that every student receives the guidance and resources they need to grow both professionally and personally.',
      attributes: {
        'data-element': 'about-story-text'
      }
    },

    // Values Section
    {
      id: valuesId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Values',
      parentId: leftId,
      hierarchyName: "About Values",
      className: aboutValuesClass,
      styles: {},
      children: [valuesTitleId, valuesListId],
      text: '',
      attributes: {
        'data-element': 'about-values'
      }
    },

    // Values Title
    {
      id: valuesTitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Values Title',
      parentId: valuesId,
      hierarchyName: "Values Title",
      className: aboutValuesTitleClass,
      styles: {},
      children: [],
      text: 'Our Core Values',
      attributes: {
        'data-element': 'about-values-title'
      }
    },

    // Values List
    {
      id: valuesListId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Values List',
      parentId: valuesId,
      hierarchyName: "Values List",
      className: aboutValuesListClass,
      styles: {},
      children: [value1Id, value2Id, value3Id, value4Id],
      text: '',
      attributes: {
        'data-element': 'about-values-list'
      }
    },

    // Value 1 - Excellence
    {
      id: value1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 1',
      parentId: valuesListId,
      hierarchyName: "Value 1",
      className: aboutValueItemClass,
      styles: {},
      children: [value1IconId, value1ContentId],
      text: '',
      attributes: {
        'data-element': 'about-value-item'
      }
    },

    {
      id: value1IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 1 Icon',
      parentId: value1Id,
      hierarchyName: "Value 1 Icon",
      className: aboutValueIconClass,
      styles: {},
      children: [],
      text: '⭐',
      attributes: {
        'data-element': 'about-value-icon'
      }
    },

    {
      id: value1ContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 1 Content',
      parentId: value1Id,
      hierarchyName: "Value 1 Content",
      className: 'value-content',
      styles: {},
      children: [value1NameId, value1DescId],
      text: '',
      attributes: {
        'data-element': 'value-content'
      }
    },

    {
      id: value1NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 1 Name',
      parentId: value1ContentId,
      hierarchyName: "Value 1 Name",
      className: aboutValueNameClass,
      styles: {},
      children: [],
      text: 'Excellence',
      attributes: {
        'data-element': 'about-value-name'
      }
    },

    {
      id: value1DescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 1 Description',
      parentId: value1ContentId,
      hierarchyName: "Value 1 Description",
      className: aboutValueDescClass,
      styles: {},
      children: [],
      text: 'Commitment to delivering the highest quality education and maintaining superior standards in all our programs.',
      attributes: {
        'data-element': 'about-value-desc'
      }
    },

    // Value 2 - Innovation
    {
      id: value2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 2',
      parentId: valuesListId,
      hierarchyName: "Value 2",
      className: aboutValueItemClass,
      styles: {},
      children: [value2IconId, value2ContentId],
      text: '',
      attributes: {
        'data-element': 'about-value-item'
      }
    },

    {
      id: value2IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 2 Icon',
      parentId: value2Id,
      hierarchyName: "Value 2 Icon",
      className: aboutValueIconClass,
      styles: {},
      children: [],
      text: '💡',
      attributes: {
        'data-element': 'about-value-icon'
      }
    },

    {
      id: value2ContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 2 Content',
      parentId: value2Id,
      hierarchyName: "Value 2 Content",
      className: 'value-content',
      styles: {},
      children: [value2NameId, value2DescId],
      text: '',
      attributes: {
        'data-element': 'value-content'
      }
    },

    {
      id: value2NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 2 Name',
      parentId: value2ContentId,
      hierarchyName: "Value 2 Name",
      className: aboutValueNameClass,
      styles: {},
      children: [],
      text: 'Innovation',
      attributes: {
        'data-element': 'about-value-name'
      }
    },

    {
      id: value2DescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 2 Description',
      parentId: value2ContentId,
      hierarchyName: "Value 2 Description",
      className: aboutValueDescClass,
      styles: {},
      children: [],
      text: 'Embracing cutting-edge technology and modern teaching methodologies to stay ahead of industry trends.',
      attributes: {
        'data-element': 'about-value-desc'
      }
    },

    // Value 3 - Integrity
    {
      id: value3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 3',
      parentId: valuesListId,
      hierarchyName: "Value 3",
      className: aboutValueItemClass,
      styles: {},
      children: [value3IconId, value3ContentId],
      text: '',
      attributes: {
        'data-element': 'about-value-item'
      }
    },

    {
      id: value3IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 3 Icon',
      parentId: value3Id,
      hierarchyName: "Value 3 Icon",
      className: aboutValueIconClass,
      styles: {},
      children: [],
      text: '🤝',
      attributes: {
        'data-element': 'about-value-icon'
      }
    },

    {
      id: value3ContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 3 Content',
      parentId: value3Id,
      hierarchyName: "Value 3 Content",
      className: 'value-content',
      styles: {},
      children: [value3NameId, value3DescId],
      text: '',
      attributes: {
        'data-element': 'value-content'
      }
    },

    {
      id: value3NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 3 Name',
      parentId: value3ContentId,
      hierarchyName: "Value 3 Name",
      className: aboutValueNameClass,
      styles: {},
      children: [],
      text: 'Integrity',
      attributes: {
        'data-element': 'about-value-name'
      }
    },

    {
      id: value3DescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 3 Description',
      parentId: value3ContentId,
      hierarchyName: "Value 3 Description",
      className: aboutValueDescClass,
      styles: {},
      children: [],
      text: 'Building trust through transparency, honesty, and ethical practices in all our educational endeavors.',
      attributes: {
        'data-element': 'about-value-desc'
      }
    },

    // Value 4 - Inclusivity
    {
      id: value4Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 4',
      parentId: valuesListId,
      hierarchyName: "Value 4",
      className: aboutValueItemClass,
      styles: {},
      children: [value4IconId, value4ContentId],
      text: '',
      attributes: {
        'data-element': 'about-value-item'
      }
    },

    {
      id: value4IconId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 4 Icon',
      parentId: value4Id,
      hierarchyName: "Value 4 Icon",
      className: aboutValueIconClass,
      styles: {},
      children: [],
      text: '🌍',
      attributes: {
        'data-element': 'about-value-icon'
      }
    },

    {
      id: value4ContentId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 4 Content',
      parentId: value4Id,
      hierarchyName: "Value 4 Content",
      className: 'value-content',
      styles: {},
      children: [value4NameId, value4DescId],
      text: '',
      attributes: {
        'data-element': 'value-content'
      }
    },

    {
      id: value4NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 4 Name',
      parentId: value4ContentId,
      hierarchyName: "Value 4 Name",
      className: aboutValueNameClass,
      styles: {},
      children: [],
      text: 'Inclusivity',
      attributes: {
        'data-element': 'about-value-name'
      }
    },

    {
      id: value4DescId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Value 4 Description',
      parentId: value4ContentId,
      hierarchyName: "Value 4 Description",
      className: aboutValueDescClass,
      styles: {},
      children: [],
      text: 'Creating an inclusive learning environment that welcomes students from all backgrounds and skill levels.',
      attributes: {
        'data-element': 'about-value-desc'
      }
    },

    // Quote Section
    {
      id: quoteId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'About Quote',
      parentId: containerId,
      hierarchyName: "About Quote",
      className: aboutQuoteClass,
      styles: {},
      children: [quoteTextId, quoteAuthorId],
      text: '',
      attributes: {
        'data-element': 'about-quote'
      }
    },

    // Quote Text
    {
      id: quoteTextId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'blockquote',
      name: 'Quote Text',
      parentId: quoteId,
      hierarchyName: "Quote Text",
      className: aboutQuoteTextClass,
      styles: {},
      children: [],
      text: '"Since our inception, we have remained committed to our core values of excellence, innovation, integrity, and inclusivity. As we look to the future, Skilled Nation Institute of Technology continues to expand its programs, partnerships, and opportunities, aiming to become a leading hub for technical education and skill development."',
      attributes: {
        'data-element': 'about-quote-text'
      }
    },

    // Quote Author
    {
      id: quoteAuthorId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'cite',
      name: 'Quote Author',
      parentId: quoteId,
      hierarchyName: "Quote Author",
      className: aboutQuoteAuthorClass,
      styles: {},
      children: [],
      text: '— Skilled Nation Institute of Technology',
      attributes: {
        'data-element': 'about-quote-author'
      }
    }
  ];

  return {
    aboutSection: elements[0],
    elements: elements
  };
}; 